import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock3 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { formatDate } from "@/utils/formatDate";
import { timesheetsApi } from "../api/timesheets";
import { useCreateTimesheet } from "../hooks/useTimesheets";
import { MONTH_NAMES } from "../types";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

function buildPeriods() {
  const periods: { year: number; month: number; label: string }[] = [];
  for (let i = 5; i >= 0; i--) {
    let m = CURRENT_MONTH - i;
    let y = CURRENT_YEAR;
    if (m <= 0) { m += 12; y -= 1; }
    periods.push({ year: y, month: m, label: `${MONTH_NAMES[m]} ${y}` });
  }
  return periods;
}
const PERIODS = buildPeriods();

interface Props {
  open: boolean;
  onClose: () => void;
  employee: { id: string; name: string };
}

export function ProxySubmitModal({ open, onClose, employee }: Props) {
  const createTimesheet = useCreateTimesheet();
  const [year, setYear] = useState(CURRENT_MONTH === 1 ? CURRENT_YEAR - 1 : CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH === 1 ? 12 : CURRENT_MONTH - 1);
  const [note, setNote] = useState("");
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [secondaryFiles, setSecondaryFiles] = useState<Record<string, File | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const { data: contractsPayload } = useQuery({
    queryKey: ["proxy-submit-contracts", employee.id, year, month],
    queryFn: () => timesheetsApi.mySecondaryContractsWithPrimary(year, month, employee.id),
    enabled: open,
  });
  const secondaryContracts = contractsPayload?.secondaries ?? [];
  const primaryInfo = contractsPayload?.primary;
  const existingTimesheets = contractsPayload?.existing ?? [];

  // A slot mid-replacement has TWO entries (withdrawal_requested + pending) —
  // any non-rejected entry means the slot is taken.
  const primaryOccupying = existingTimesheets.filter(
    (e) => e.secondary_contract === null && e.status !== "rejected",
  );
  const canSubmitPrimary = primaryOccupying.length === 0;
  const existingSecondaryIds = new Set(
    existingTimesheets
      .filter((e) => e.secondary_contract !== null && e.status !== "rejected")
      .map((e) => e.secondary_contract as string),
  );

  const PRIMARY_LABELS: Record<string, string> = {
    uop: "Umowa o pracę",
    uz: "Umowa zlecenie",
    b2b: "Kontrakt B2B",
  };
  const primaryKindLabel = PRIMARY_LABELS[primaryInfo?.contract_type ?? "uop"] ?? "Umowa";
  const primaryCompany = primaryInfo?.company_short_code ?? primaryInfo?.company_name ?? null;

  const reset = () => {
    setNote("");
    setPrimaryFile(null);
    setSecondaryFiles({});
    setErrors({});
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    const e: Record<string, string> = {};
    if (canSubmitPrimary && !primaryFile) {
      e.primary = "Skan karty podstawowej jest wymagany.";
    }
    const anySecondaryToUpload = Object.entries(secondaryFiles).some(
      ([cid, f]) => f && !existingSecondaryIds.has(cid),
    );
    if (!canSubmitPrimary && !anySecondaryToUpload) {
      e.submit = "Nie wybrano żadnego pliku do złożenia.";
    }
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setErrors({});
    setSubmitting(true);

    const uploadOne = async (scan: File, secondaryContractId: string | null) =>
      new Promise<void>((resolve, reject) => {
        const fd = new FormData();
        fd.append("year", String(year));
        fd.append("month", String(month));
        fd.append("note", note);
        fd.append("scan", scan);
        fd.append("employee", employee.id);
        if (secondaryContractId) fd.append("secondary_contract", secondaryContractId);
        createTimesheet.mutate(fd, {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        });
      });

    try {
      if (canSubmitPrimary && primaryFile) {
        await uploadOne(primaryFile, null);
      }
      const activeIds = new Set(
        secondaryContracts.filter((c) => c.is_active_in_period).map((c) => c.id),
      );
      for (const [contractId, file] of Object.entries(secondaryFiles)) {
        if (file && activeIds.has(contractId) && !existingSecondaryIds.has(contractId)) {
          await uploadOne(file, contractId);
        }
      }
      handleClose();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
        ?.response?.data?.errors?.detail;
      setErrors({ submit: detail ?? "Błąd podczas składania kart. Spróbuj ponownie." });
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Złóż kartę godzin — ${employee.name}`}>
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Okres <span className="text-red-500">*</span>
          </label>
          <select
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            value={`${year}-${month}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-").map(Number);
              setYear(y);
              setMonth(m);
            }}
          >
            {PERIODS.map((p) => (
              <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
          </label>
          <textarea
            rows={2}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Dodatkowe informacje..."
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            {primaryKindLabel}
            {primaryCompany ? ` — ${primaryCompany}` : ""}{" "}
            {canSubmitPrimary && <span className="text-red-500">*</span>}
          </label>
          {canSubmitPrimary ? (
            <>
              <FileDropZone
                files={primaryFile ? [primaryFile] : []}
                onFilesChange={(fs) => setPrimaryFile(fs[0] ?? null)}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              {errors.primary && <p className="text-xs text-red-500 mt-1">{errors.primary}</p>}
            </>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
              Karta {primaryKindLabel.toLowerCase()} za {month.toString().padStart(2, "0")}/{year}{" "}
              jest już złożona{" "}
              {primaryOccupying.some((e) => e.status === "withdrawal_requested") ? (
                <Badge color="orange">wymiana w toku</Badge>
              ) : (
                <>
                  {primaryOccupying.some((e) => e.status === "approved") && <Badge color="green">zatwierdzona</Badge>}
                  {primaryOccupying.some((e) => e.status === "pending") && <Badge color="yellow">oczekuje</Badge>}
                </>
              )}
              .
            </div>
          )}
        </div>

        {secondaryContracts.map((sc) => {
          const company = sc.company_short_code ?? sc.company_name;
          const inactive = !sc.is_active_in_period;
          const periodNote = inactive
            ? sc.effective_to
              ? `Umowa: ${formatDate(sc.effective_from)} – ${formatDate(sc.effective_to)} (nie obejmuje wybranego okresu)`
              : `Umowa rozpoczyna się ${formatDate(sc.effective_from)} — wybierz późniejszy okres`
            : null;
          const alreadySubmitted = existingSecondaryIds.has(sc.id);
          const slotEntries = existingTimesheets.filter(
            (e) => e.secondary_contract === sc.id && e.status !== "rejected",
          );
          return (
            <div key={sc.id} className={inactive ? "opacity-60" : ""}>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Umowa zlecenie{company ? ` — ${company}` : ""}{" "}
                {!alreadySubmitted && (
                  <span className="text-gray-400 text-xs font-normal">(opcjonalne)</span>
                )}
              </label>
              {inactive ? (
                <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400 italic">
                  {periodNote}
                </div>
              ) : alreadySubmitted ? (
                <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  Karta UZ za {month.toString().padStart(2, "0")}/{year} jest już złożona{" "}
                  {slotEntries.some((e) => e.status === "withdrawal_requested") ? (
                    <Badge color="orange">wymiana w toku</Badge>
                  ) : (
                    <>
                      {slotEntries.some((e) => e.status === "approved") && <Badge color="green">zatwierdzona</Badge>}
                      {slotEntries.some((e) => e.status === "pending") && <Badge color="yellow">oczekuje</Badge>}
                    </>
                  )}
                  .
                </div>
              ) : (
                <FileDropZone
                  files={secondaryFiles[sc.id] ? [secondaryFiles[sc.id]!] : []}
                  onFilesChange={(fs) =>
                    setSecondaryFiles((prev) => ({ ...prev, [sc.id]: fs[0] ?? null }))
                  }
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              )}
            </div>
          );
        })}

        {errors.submit && <p className="text-sm text-red-600">{errors.submit}</p>}

        <div className="flex gap-3">
          <Button loading={submitting} onClick={submit}>
            <Clock3 size={14} className="mr-1.5" /> Złóż kartę
          </Button>
          <Button variant="secondary" onClick={handleClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}
